<script setup>
import NavButton from "@/Components/Navigation/NavButton.vue";
import NavLink from "@/Components/Navigation/NavLink.vue";
import NavSubLink from "@/Components/Navigation/NavSubLink.vue";
import {
    ArrowRightStartOnRectangleIcon as LogoutIcon,
    BanknotesIcon,
    ClipboardDocumentListIcon,
    DocumentTextIcon,
    NewspaperIcon,
    QueueListIcon,
    UserIcon,
    PhoneIcon,
} from "@heroicons/vue/24/outline";
import { Link, usePage } from "@inertiajs/vue3";

const page = usePage();

const unreadArticleCount = page.props.unreadArticleCount;
const unseenDealCount = page.props.unseenDealCount;
const unseenDocumentCount = page.props.unseenDocumentCount;

defineProps({
    showHeaderActions: {
        type: Boolean,
        default: true,
    },
    mobile: {
        type: Boolean,
        default: false,
    },
});
</script>

<template>
    <template
        v-if="
            $page.props.auth.user !== null &&
            ($page.props.auth.user?.two_factor_confirmed_at ||
                $page.props.twoFactorRequired === false ||
                $page.props.isImpersonated)
        "
    >
        <NavLink :href="route('deals.index')" :active="route().current('deals.*')" class="relative">
            <BanknotesIcon class="nav-icon" />
            {{ trans.get("navigation.deals") }}
            <span
                v-if="unseenDealCount > 0"
                class="ml-1 bg-red-600 text-white text-xs h-5 min-w-5 px-1.5 flex justify-center items-center rounded-full leading-none"
            >
                {{ unseenDealCount }}
            </span>
        </NavLink>

        <NavLink
            v-if="$page.props.auth.user.view_news"
            :href="route('article.index')"
            :active="route().current('article.*')"
            class="relative"
        >
            <NewspaperIcon class="nav-icon" />
            {{ trans.get("navigation.news") }}
            <span
                v-if="unreadArticleCount > 0"
                class="ml-1 bg-red-600 text-white text-xs h-5 min-w-5 px-1.5 flex justify-center items-center rounded-full leading-none"
            >
                {{ unreadArticleCount }}
            </span>
        </NavLink>

        <NavLink
            v-if="$page.props.auth.user.view_documents"
            :href="route('documents.index')"
            :active="route().current('documents.*')"
            class="relative"
        >
            <DocumentTextIcon class="nav-icon" />
            {{ trans.get("navigation.documents") }}
            <span
                v-if="unseenDocumentCount > 0"
                class="ml-1 bg-red-600 text-white text-xs h-5 min-w-5 px-1.5 flex justify-center items-center rounded-full leading-none"
            >
                {{ unseenDocumentCount }}
            </span>
        </NavLink>

        <NavLink :href="route('customer-numbers.index')" :active="route().current('customer-numbers.*')">
            <QueueListIcon class="nav-icon" />
            {{ trans.get("navigation.customer-numbers") }}
        </NavLink>

        <li
            v-if="showHeaderActions && $page.props.permissions.view_invoice_requests"
            class="xl:h-[68px] flex items-center"
        >
            <Link
                :href="route('invoice-requests.index')"
                class="flex gap-3 xl:gap-2 py-3 xl:py-2.5 px-4 items-center rounded-xl font-bold transition-all duration-300 bg-theme-primary text-white hover:-translate-y-0.5 hover:brightness-95"
                :class="route().current('invoice-requests.*') ? 'ring-2 ring-theme-primary ring-offset-2' : ''"
            >
                <ClipboardDocumentListIcon class="nav-icon" />
                {{ trans.get("navigation.invoice-requests") }}
            </Link>
        </li>

        <NavButton v-if="showHeaderActions" :active="route().current('profile.*')">
            <template #menu>
                <UserIcon class="size-5" />
                <span class="xl:sr-only">{{ trans.get("navigation.account") }}</span>
            </template>

            <template #dropdown>
                <ul class="space-y-1">
                    <NavSubLink :href="route('profile.show')" :active="route().current('profile.*')">
                        <UserIcon class="nav-icon" />
                        {{ trans.get("navigation.profile") }}
                    </NavSubLink>
                    <NavSubLink v-if="$page.props.isImpersonated" :href="route('logout.impersonation')" method="post">
                        <LogoutIcon class="nav-icon" />
                        {{ trans.get("navigation.logout") }}
                    </NavSubLink>
                    <NavSubLink v-else :href="route('logout')" method="post">
                        <LogoutIcon class="nav-icon" />
                        {{ trans.get("navigation.logout") }}
                    </NavSubLink>
                </ul>
            </template>
        </NavButton>
        <li class="xl:h-[68px] flex items-center xl:hidden">
            <a
                class="hover:text-theme-primary flex gap-4 xl:gap-2 py-3 px-3 items-center transition-colors duration-300"
                :href="'tel:' + page.props.currentTenant.phone"
            >
                <PhoneIcon class="nav-icon" />

                <span>
                    {{ page.props.currentTenant.phone }}
                </span>
            </a>
        </li>
    </template>
</template>
